import { useCallback, useEffect, useRef, useState } from "react";
import type { EEGTileTransportMode } from "../services/EEGSegmentCache";

/**
 * Paused high-speed "inspect" hold extracted from EEGViewer to keep the main
 * component focused on rendering. When the reviewer pauses while playing
 * back at a high speed, the viewer briefly "holds" the high-speed
 * playback/resolution context (rather than snapping straight to the
 * clinical inspect context) so a quick resume doesn't re-trigger an
 * expensive resolution/buffer swap. This hook owns that hold's state + the
 * timeout-driven effect that auto-clears it once it expires (or once
 * playback resumes).
 *
 * Everything that *creates* a hold, or reacts to one being active/cleared to
 * reset tile/window state (auto-resolution alignment, paused-play-hold
 * window restore, etc.), stays in EEGViewer since it is tightly coupled to
 * the playback tick / buffer machinery there.
 */

export interface PausedHighSpeedInspectHold {
  untilMs: number;
  playbackSpeed: number;
  timeScale: number;
  resolutionLevel: number;
  transportMode: EEGTileTransportMode;
}

export interface UseEEGPausedInspectHoldParams {
  isPlaying: boolean;
}

export function useEEGPausedInspectHold(
  params: UseEEGPausedInspectHoldParams,
) {
  const { isPlaying } = params;

  const [pausedHighSpeedInspectHold, setPausedHighSpeedInspectHold] =
    useState<PausedHighSpeedInspectHold | null>(null);
  const pausedHighSpeedInspectHoldRef =
    useRef<PausedHighSpeedInspectHold | null>(null);
  pausedHighSpeedInspectHoldRef.current = pausedHighSpeedInspectHold;

  const commitPausedHighSpeedInspectHold = useCallback(
    (nextHold: PausedHighSpeedInspectHold | null) => {
      pausedHighSpeedInspectHoldRef.current = nextHold;
      setPausedHighSpeedInspectHold(nextHold);
    },
    [],
  );

  // Populated by EEGViewer before a hold is committed when it wants the
  // subsequent paused-inspect window to start at a specific time (rather
  // than the current playhead). Consumed by EEGViewer's window-reset effects.
  const pendingPausedInspectWindowStartRef = useRef<number | null>(null);

  useEffect(() => {
    if (!pausedHighSpeedInspectHold) {
      return;
    }

    if (isPlaying) {
      commitPausedHighSpeedInspectHold(null);
      return;
    }

    const remainingMs = pausedHighSpeedInspectHold.untilMs - Date.now();
    if (remainingMs <= 0) {
      commitPausedHighSpeedInspectHold(null);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (
        pausedHighSpeedInspectHoldRef.current?.untilMs ===
        pausedHighSpeedInspectHold.untilMs
      ) {
        commitPausedHighSpeedInspectHold(null);
      }
    }, remainingMs);

    return () => window.clearTimeout(timeoutId);
  }, [commitPausedHighSpeedInspectHold, isPlaying, pausedHighSpeedInspectHold]);

  return {
    pausedHighSpeedInspectHold,
    pausedHighSpeedInspectHoldRef,
    commitPausedHighSpeedInspectHold,
    pendingPausedInspectWindowStartRef,
  };
}
